import "react-international-phone/style.css"

import { CaretDownFilled } from "@ant-design/icons"
import { Button, notification, Popover } from "antd"
import type { BaseButtonProps } from "antd/es/button/button"
import { NotificationInstance } from "antd/es/notification/interface"
import { TooltipRef } from "antd/es/tooltip"
import { Copy, CopyCheck, Phone, PhoneOff } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { fetchDialerPhoneNumber } from "~/api/marketing-os"
import Loader, { OSLoader } from "~/components/base-loader"
import { EVENTS } from "~/containers/marketing-os/constants"
import { useCopyToClipboard } from "~/hooks/useClipboard"
import useGlobalStore from "~/store/global"
import useMarketingOsStore from "~/store/marketing-os"
import { ITokenInfo } from "~/types/global"
import { IMarketingOsContactInList } from "~/types/marketing-os"
import { track } from "~/utils/amplitude"
import { getBaseEventProps } from "~/utils/amplitude-events"
import { formatPhoneNumber } from "~/utils/format-phone-number"
import { sleep } from "~/utils/sleep"

import CustomTooltip from "./custom-tooltip"

type Props = {
  children?: React.ReactNode
  person: IMarketingOsContactInList
  icon?: React.ReactNode
  className?: string
  notificationsApi?: NotificationInstance
  canAddNewNumber?: boolean
  type?: BaseButtonProps["type"]
  onExclusionChange?: (
    isExclusion: boolean,
    exclusionReason: string | null
  ) => void
}

const getLimitErrorMessages = (
  t: any
): Record<
  "TENANT_MAX_LIMIT_REACH" | "USER_MAX_LIMIT_REACH",
  { message: string; description: string }
> => ({
  TENANT_MAX_LIMIT_REACH: {
    message: t("components.label_tenant_phone_limit"),
    description: t("components.label_tenant_phone_limit_desc")
  },
  USER_MAX_LIMIT_REACH: {
    message: t("components.label_user_phone_limit"),
    description: t("components.label_user_phone_limit_desc")
  }
})

const recursiveFindMobileNumber = async (
  tokenInfo: ITokenInfo,
  person: IMarketingOsContactInList,
  notificationApi?: any,
  t?: any
): Promise<{
  phones: any[]
  personIspId: string
  is_exclusion: boolean
  exclusion_reason: string | null
  errorCode?: "TENANT_MAX_LIMIT_REACH" | "USER_MAX_LIMIT_REACH"
}> => {
  try {
    const response = await fetchDialerPhoneNumber(
      tokenInfo!,
      [
        {
          name: person.name,
          linkedin_url: person.linkedin_url,
          seniority: person.seniority,
          account: person.account,
          location: person.location
        }
      ],
      true,
      true
    )

    const errorCode = response?.data?.error_code as
      | "TENANT_MAX_LIMIT_REACH"
      | "USER_MAX_LIMIT_REACH"
      | undefined
    const personData = response.data?.data?.[0]
    const phones = personData?.phones
    const is_exclusion: boolean = personData?.is_exclusion ?? false
    const exclusion_reason: string | null = personData?.exclusion_reason ?? null

    const LIMIT_ERROR_MESSAGES = t ? getLimitErrorMessages(t) : null
    if (errorCode && LIMIT_ERROR_MESSAGES?.[errorCode]) {
      const { message, description } = LIMIT_ERROR_MESSAGES[errorCode]
      notificationApi?.error({ message, description })
      const personIspId = personData?.id
      return { phones, personIspId, is_exclusion, exclusion_reason, errorCode }
    }

    const isPending = phones.find((phone: any) =>
      ["PENDING"].includes(phone.phone_status)
    )
    if (isPending) {
      const { promise, clear } = sleep(30000)
      await promise
      const result = await recursiveFindMobileNumber(
        tokenInfo!,
        person,
        notificationApi,
        t
      )
      clear()
      return result
    } else {
      const personIspId = personData?.id
      return { phones, personIspId, is_exclusion, exclusion_reason }
    }
  } catch {
    throw new Error("No mobile number")
  }
}

const MobileNumberPopoverContent = ({
  mobileNumbers = [],
  isMobileFetching,
  is_exclusion,
  exclusion_reason
}: {
  mobileNumbers: any[]
  isMobileFetching: boolean
  closePopover?: () => void
  is_exclusion: boolean
  exclusion_reason: string | null
}) => {
  const { t } = useTranslation()
  const [copiedText, copy] = useCopyToClipboard()

  return (
    <div className="flex flex-col gap-3 text-dark-6 dark:text-dark-2 min-w-[280px] max-w-[280px]">
      {mobileNumbers?.length > 0
        ? mobileNumbers.map((mobile, index: number) => {
            return (
              <div
                key={mobile.id}
                className={`flex flex-col gap-1 pb-3 border-gray-400 last:border-none last:pb-0 text-dark-2 dark:text-dark-6 ${index + 1 === mobileNumbers.length ? "" : "border-gray-400  border-b-[0.5px]"}`}
              >
                <p className="text-13 font-medium">
                  {t("components.label_phone_number")}
                </p>
                <div className="flex justify-between">
                  <div className="flex items-center gap-2">
                    <CustomTooltip
                      title={is_exclusion ? exclusion_reason : null}
                    >
                      <p className="text-xs dark:text-primary-light text-primary">
                        {formatPhoneNumber(mobile.phone)}
                      </p>
                    </CustomTooltip>
                    <button
                      onClick={() => {
                        track(EVENTS.MOBILE_NUMBER_COPY, {
                          ...getBaseEventProps({
                            feature: "marketing_os",
                            accountName: mobile.account
                          }),
                          phone_source: mobile.phone_source,
                          phone_id: mobile.id
                        })
                        copy(`${mobile.phone}`)
                      }}
                    >
                      {copiedText === mobile.phone ? (
                        <CopyCheck size={16} className="text-green-500" />
                      ) : (
                        <Copy size={16} />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )
          })
        : null}

      <div className="flex gap-3 w-full">
        {isMobileFetching && <OSLoader className="xs" />}
        <div className="flex flex-col gap-2 w-full">
          {isMobileFetching && (
            <p className="text-xs text-gray-400">
              {t("components.label_fetching_phone_number_time")}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

const MobileNumberPopover = ({
  mobileNumbers,
  children,
  isMobileFetching,
  is_exclusion,
  exclusion_reason
}: {
  mobileNumbers: any[]
  children: React.ReactNode
  isMobileFetching: boolean
  is_exclusion: boolean
  exclusion_reason: string | null
}) => {
  const popOverRef = useRef<TooltipRef>(null)
  const closePopover = useCallback(
    () => popOverRef.current?.nativeElement.click(),
    [popOverRef]
  )
  return (
    <Popover
      ref={popOverRef}
      destroyOnHidden
      content={
        (mobileNumbers && mobileNumbers.length > 0) || isMobileFetching ? (
          <MobileNumberPopoverContent
            mobileNumbers={mobileNumbers}
            closePopover={closePopover}
            isMobileFetching={isMobileFetching}
            is_exclusion={is_exclusion}
            exclusion_reason={exclusion_reason}
          />
        ) : null
      }
      title={null}
      arrow={false}
      trigger="click"
      placement="bottomLeft"
    >
      {children}
    </Popover>
  )
}

export default function GetMobileNumber({
  children,
  className = "text-xs p-0 h-fit text-primary hover:underline dark:hover:!text-dark-6 dark:text-primary-light",
  icon = (
    <Phone
      size={14}
      className="dark:!text-gray-300 !text-dark-2 w-4 h-4 min-w-4 min-h-4"
    />
  ),
  person,
  type = "link",
  onExclusionChange
}: Props) {
  const updatedPhoneNumbers = useMarketingOsStore((store) =>
    store.phoneNumbersFetched.get(person?.linkedin_url)
  )
  const storedExclusionInfo = useMarketingOsStore((store) =>
    store.exclusionInfoFetched.get(person?.linkedin_url)
  )
  const setExclusionInfoFetched = useMarketingOsStore(
    (store) => store.setExclusionInfoFetched
  )
  const { t } = useTranslation()
  const [notificationApi, notificationContextHolder] =
    notification.useNotification()
  const phonesLoading = useMarketingOsStore((store) =>
    store.phoneNumbersLoading.get(person?.linkedin_url)
  )
  const setPhoneNumbersLoading = useMarketingOsStore(
    (store) => store.setPhoneNumbersLoading
  )
  const setPhoneNumbersFetched = useMarketingOsStore(
    (store) => store.setPhoneNumbersFetched
  )
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  const [mobileNumbers, setMobileNumbers] = useState<any[]>(
    () => person.phones || []
  )
  const [mobileNumberStatus, setMobileNumberStatus] = useState(() => {
    return person.phones?.find(
      (phone: any) => phone.phone_status === "FINISHED"
    )
      ? "EXISTS"
      : "UNKNOWN"
  })
  const [isMobileFetching, setMobileFetching] = useState(false)
  const [exclusionInfo, setExclusionInfo] = useState<{
    is_exclusion: boolean
    exclusion_reason: string | null
  }>(() => ({
    is_exclusion:
      storedExclusionInfo?.is_exclusion ?? person.is_exclusion ?? false,
    exclusion_reason:
      storedExclusionInfo?.exclusion_reason ?? person.exclusion_reason ?? null
  }))

  const onClick = useCallback(async () => {
    if (isMobileFetching) return
    setMobileFetching(true)
    setPhoneNumbersLoading(`${person.linkedin_url}`, true)
    track(EVENTS.MOBILE_NUMBER_FETCH, {
      ...getBaseEventProps({
        feature: "marketing_os",
        accountName: person.account
      }),
      status: "started"
    })
    const isFinished = person.phones?.filter(
      (phone: any) => phone.phone_status === "FINISHED"
    )
    if (isFinished?.length > 0) {
      setMobileNumbers(person.phones)
      setMobileNumberStatus("EXISTS")
      setMobileFetching(false)
      setPhoneNumbersLoading(`${person.linkedin_url}`, false)
      track(EVENTS.MOBILE_NUMBER_FETCH, {
        ...getBaseEventProps({
          feature: "marketing_os",
          accountName: person.account
        }),
        status: "success",
        source: "cache"
      })
      const cachedExclusion = {
        is_exclusion: person.is_exclusion ?? false,
        exclusion_reason: person.exclusion_reason ?? null
      }
      setExclusionInfo(cachedExclusion)
      setExclusionInfoFetched(`${person.linkedin_url}`, cachedExclusion)
      return
    }
    try {
      const responses = await recursiveFindMobileNumber(
        tokenInfo!,
        person,
        notificationApi,
        t
      )
      const {
        phones,
        is_exclusion,
        exclusion_reason
      }: {
        phones: any[]
        personIspId: string
        is_exclusion: boolean
        exclusion_reason: string | null
      } = responses ?? {
        phones: [],
        is_exclusion: false,
        exclusion_reason: null
      }
      setPhoneNumbersFetched(`${person.linkedin_url}`, phones)
      setMobileNumbers(phones)
      setMobileNumberStatus("EXISTS")
      setExclusionInfo({ is_exclusion, exclusion_reason })
      setExclusionInfoFetched(`${person.linkedin_url}`, {
        is_exclusion,
        exclusion_reason
      })
      track(EVENTS.MOBILE_NUMBER_FETCH, {
        ...getBaseEventProps({
          feature: "marketing_os",
          accountName: person.account
        }),
        status: "success",
        source: "api"
      })
    } catch {
      setMobileNumberStatus("NOT_EXISTS")
      track(EVENTS.MOBILE_NUMBER_FETCH, {
        ...getBaseEventProps({
          feature: "marketing_os",
          accountName: person.account
        }),
        status: "failed"
      })
    } finally {
      setMobileFetching(false)
      setPhoneNumbersLoading(`${person.linkedin_url}`, false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [person, isMobileFetching])

  const filteredMobileNumbers = mobileNumbers?.reduce((acc, cur: any) => {
    if (cur.is_active && cur.phone && cur.phone !== "NA") {
      if (cur.phone === "xxxxxxxxxx" && !exclusionInfo.is_exclusion) return acc
      if (acc.some((n: any) => n.phone === cur.phone)) return acc
      acc.push(cur)
    }
    return acc
  }, [] as any[])

  useEffect(() => {
    if (updatedPhoneNumbers) {
      setMobileNumberStatus("EXISTS")
      setMobileNumbers(updatedPhoneNumbers)
    }
  }, [updatedPhoneNumbers])

  useEffect(() => {
    if (typeof phonesLoading === "boolean") {
      setMobileFetching(phonesLoading)
    }
  }, [phonesLoading])

  useEffect(() => {
    if (exclusionInfo.is_exclusion) {
      onExclusionChange?.(true, exclusionInfo.exclusion_reason)
    }
  }, [exclusionInfo.is_exclusion])

  return (
    <MobileNumberPopover
      mobileNumbers={filteredMobileNumbers}
      isMobileFetching={isMobileFetching}
      is_exclusion={exclusionInfo.is_exclusion}
      exclusion_reason={exclusionInfo.exclusion_reason}
    >
      {notificationContextHolder}
      {mobileNumberStatus === "EXISTS" ||
      mobileNumberStatus === "NOT_EXISTS" ? (
        !filteredMobileNumbers || filteredMobileNumbers?.length === 0 ? (
          <button className="flex items-center gap-2 text-red-500 text-xs">
            <PhoneOff
              size={14}
              className="dark:!text-gray-300 !text-dark-2 w-4 h-4 min-w-4 min-h-4"
            />
            <p className="text-xs flex-1">
              {t("components.label_no_phone_number")}
            </p>
          </button>
        ) : (
          <button className="flex items-center gap-2 justify-start text-left text-primary dark:text-primary-light">
            <Phone
              size={14}
              className="dark:!text-gray-300 !text-dark-2 !w-4 !h-4 !min-w-4 !min-h-4"
            />
            <div className="flex flex-col gap-1 text-xs">
              <CustomTooltip
                title={
                  exclusionInfo.is_exclusion
                    ? exclusionInfo.exclusion_reason
                    : null
                }
              >
                <p className={exclusionInfo.is_exclusion ? "blur-sm" : ""}>
                  {filteredMobileNumbers
                    ?.slice(0, 2)
                    .map((num: any) => formatPhoneNumber(num.phone))
                    .join(", ")}
                </p>
              </CustomTooltip>
              {filteredMobileNumbers?.length > 2 && (
                <p className="w-fit items-end dark:text-dark-6 text-dark-2">{`+ ${filteredMobileNumbers.slice(2).length} number${filteredMobileNumbers.slice(2).length > 1 ? "s" : ""}`}</p>
              )}
            </div>
            {!exclusionInfo.is_exclusion && (
              <CaretDownFilled
                size={14}
                className="dark:!text-gray-300 text-dark-2 w-4 h-4 min-w-4 min-h-4"
              />
            )}
          </button>
        )
      ) : (
        <CustomTooltip
          title={
            <p className="text-xs">
              {t("components.label_fetching_phone_number_time")}
            </p>
          }
        >
          <Button
            className={className}
            onClick={onClick}
            type={type}
            id={`get-mobile-${person.person_id || person.id}`}
            data-testid="get-mobile"
          >
            {isMobileFetching ? (
              <Loader
                style={{
                  fontSize: 12,
                  color: type === "primary" ? "#fff" : "#6c5ce7"
                }}
              />
            ) : (
              icon
            )}
            {children}
          </Button>
        </CustomTooltip>
      )}
    </MobileNumberPopover>
  )
}
